import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PortfolioInsightsDividends = ({ holdings, baseCurrency, dividendData = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const symbols = [...new Set(holdings.map(h => h.symbol))];

    // Get total quantity for a symbol (aggregated from all holdings)
    const getQuantity = (symbol) => {
        return holdings
            .filter(h => h.symbol === symbol)
            .reduce((sum, h) => sum + h.quantity, 0);
    };

    const getDividendForSymbol = (symbol) => {
        return dividendData.find(d => d.symbol === symbol);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === 0) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: baseCurrency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '--';
        return `${value.toFixed(2)}%`;
    };

    const formatGrowth = (value) => {
        if (value === null || value === undefined) return '--';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(1)}%`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '--';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getGrowthColor = (value) => {
        if (value === null || value === undefined) return theme.palette.text.secondary;
        if (value > 0) return theme.palette.success.main;
        if (value < 0) return theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    // Calculate total annual dividend income
    const calculateTotalAnnualIncome = () => {
        let total = 0;
        symbols.forEach(symbol => {
            const dividend = getDividendForSymbol(symbol);
            const quantity = getQuantity(symbol);
            if (dividend?.dividend?.amount && quantity) {
                total += dividend.dividend.amount * quantity;
            }
        });
        return total;
    };

    // Count dividend-paying stocks
    const getDividendPayingCount = () => {
        return dividendData.filter(d => d.dividend?.amount > 0).length;
    };

    const totalAnnualIncome = calculateTotalAnnualIncome();
    const dividendPayingCount = getDividendPayingCount();

    // Filter to only show dividend-paying stocks in table
    const dividendPayingSymbols = symbols.filter(symbol => {
        const dividend = getDividendForSymbol(symbol);
        return dividend?.dividend?.amount > 0;
    });

    return (
        <Box>
            {/* Summary */}
            {/* Summary */}
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: 'center',  // ADD THIS for mobile centering too
                gap: 2,
                mb: 3,
                width: '100%'  // ADD THIS
            }}>
                <Box sx={{
                    p: 2,
                    bgcolor: theme.palette.background.default,
                    borderRadius: 2,
                    textAlign: 'center',
                    minWidth: 200,
                    maxWidth: 280,
                }}>
                    <Typography variant="caption" color="textSecondary">
                        Est. Annual Dividend Income
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                        {formatCurrency(totalAnnualIncome)}
                    </Typography>
                </Box>
                <Box sx={{
                    p: 2,
                    bgcolor: theme.palette.background.default,
                    borderRadius: 2,
                    textAlign: 'center',
                    minWidth: 200,
                    maxWidth: 280,
                }}>
                    <Typography variant="caption" color="textSecondary">
                        Dividend-Paying Holdings
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                        {dividendPayingCount} / {symbols.length}
                    </Typography>
                </Box>
            </Box>

            {/* Table */}
            {dividendPayingSymbols.length > 0 ? (
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{
                        minWidth: isMobile ? 450 : 'auto',
                        '& .MuiTableCell-root': {
                            px: isMobile ? 1 : 2,  // tighter padding on mobile
                            whiteSpace: 'nowrap'
                        }
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell align="right">{isMobile ? 'Ann Div' : 'Annual Div'}</TableCell>
                                <TableCell align="right">Yield</TableCell>
                                <TableCell align="center">{isMobile ? 'Freq' : 'Frequency'}</TableCell>
                                <TableCell align="right">{isMobile ? 'Ex-Div' : 'Ex-Div Date'}</TableCell>
                                <TableCell align="right">{isMobile ? '1Y Grw' : '1Y Growth'}</TableCell>
                                <TableCell align="right">{isMobile ? 'Income' : 'Annual Income'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dividendPayingSymbols.map(symbol => {
                                const dividend = getDividendForSymbol(symbol);
                                const quantity = getQuantity(symbol);
                                const annualIncome = dividend?.dividend?.amount
                                    ? dividend.dividend.amount * quantity
                                    : 0;

                                return (
                                    <TableRow key={symbol}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {symbol}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(dividend?.dividend?.amount)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatPercent(dividend?.dividend?.yield)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {dividend?.dividend?.frequency || '--'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatDate(dividend?.dividend?.exDivDate)}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ color: getGrowthColor(dividend?.dividend?.growth) }}
                                        >
                                            {formatGrowth(dividend?.dividend?.growth)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium" color="success.main">
                                                {formatCurrency(annualIncome)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No dividend-paying stocks in your portfolio
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default PortfolioInsightsDividends;